import * as Sentry from '@sentry/react'
import { apiTraceTargets, scrubSentryEvent } from './observability'

/**
 * Initialize Sentry error monitoring for Portal application.
 * Portal-specific configuration includes:
 * - Blocking Stripe elements from session replay
 * - Masking all inputs for payment data privacy
 * - Portal-specific error filtering
 */
export function initSentry() {
  if (!import.meta.env.VITE_SENTRY_DSN) {
    console.warn('Sentry DSN not configured, skipping initialization')
    return
  }

  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.VITE_SENTRY_ENVIRONMENT || 'development',
    release: __SENTRY_RELEASE__ || undefined,
    initialScope: { tags: { app: 'portal' } },

    // Performance Monitoring
    tracesSampleRate: parseFloat(
      import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE || '1.0'
    ),

    // Session Replay is error-only by default: no unprompted session recordings.
    replaysSessionSampleRate: parseFloat(
      import.meta.env.VITE_SENTRY_REPLAYS_SESSION_SAMPLE_RATE || '0'
    ),
    replaysOnErrorSampleRate: parseFloat(
      import.meta.env.VITE_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE || '1.0'
    ),

    // Trace all outgoing requests to the backend
    tracePropagationTargets: apiTraceTargets(import.meta.env.VITE_API_URL),

    integrations: [
      // Browser Tracing for performance monitoring
      Sentry.browserTracingIntegration(),
      // Session Replay with privacy controls for payment data
      Sentry.replayIntegration({
        // Don't mask all text to allow debugging, but protect sensitive inputs
        maskAllText: false,
        // Don't block media by default
        blockAllMedia: false,
        // Mask ALL inputs for payment data privacy
        maskAllInputs: true,
        // Don't capture Stripe iframe content
        block: ['.StripeElement', '#card-element', '[data-stripe]'],
        // Network request/response capture for API debugging
        networkDetailAllowUrls: apiTraceTargets(import.meta.env.VITE_API_URL),
        // Portal RPC bodies and Authorization tokens contain customer/payment
        // data. Correlation headers provide the debugging link without copying
        // those payloads into Replay.
        networkCaptureBodies: false,
        networkRequestHeaders: ['X-Request-ID'],
        networkResponseHeaders: ['X-Request-ID', 'X-Trace-ID'],
      }),
    ],

    // Portal-specific error filtering
    ignoreErrors: [
      // Browser extensions
      /^chrome-extension:\/\//,
      /^moz-extension:\/\//,
      // Stale JS chunks after deployment — Vercel SPA rewrite serves HTML
      // for missing hashed assets, and the browser rejects the MIME type.
      // A page refresh resolves it. (Sentry CORTADO-PORTAL-9)
      "is not a valid JavaScript MIME type",
      // Auth redirects (not real errors)
      'CodeUnauthenticated',
      // Network-level failures from the browser fetch — user offline, tunnel,
      // CORS preflight drop, OS sleep mid-request. Not actionable on our side.
      'Failed to fetch',
      'NetworkError when attempting to fetch resource',
      'Load failed',
      // Stripe-related expected errors that users see
      'Your card was declined',
      'Your card has expired',
      'Your card has insufficient funds',
      'Your card number is incorrect',
      'Your card\'s security code is incorrect',
      'Your card\'s expiration date is incorrect',
      // Stripe validation errors (expected)
      /^stripe/i,
    ],

    // Add additional context before sending
    beforeSend(event, _hint) {
      // Don't send events in development unless explicitly enabled
      if (import.meta.env.DEV && !import.meta.env.VITE_SENTRY_DEBUG) {
        console.log('[Sentry Debug]', event)
        return null
      }

      return scrubSentryEvent(event)
    },
    beforeSendTransaction: scrubSentryEvent,
    beforeBreadcrumb: scrubSentryEvent,
  })
}

/**
 * Set portal session context (quote/invoice being viewed).
 * This helps correlate errors with specific business entities.
 */
export function setPortalContext(context: {
  quoteId?: string
  invoiceId?: string
  organizationId?: string
}) {
  if (context.quoteId) Sentry.setTag('portal.quote_id', context.quoteId)
  if (context.invoiceId) Sentry.setTag('portal.invoice_id', context.invoiceId)
  if (context.organizationId) Sentry.setTag('portal.organization_id', context.organizationId)
}

/**
 * Set customer context for better error attribution.
 * Call this when customer information is loaded.
 */
export function setCustomerContext(customer: {
  id?: string
  email?: string
  name?: string
}) {
  if (customer.id || customer.email || customer.name) {
    Sentry.setUser({
      id: customer.id,
      email: customer.email,
      username: customer.name,
    })
  }
}

/**
 * Clear customer context on logout or session end.
 */
export function clearCustomerContext() {
  Sentry.setUser(null)
}

/**
 * Helper to capture custom events with context.
 */
export function captureError(error: Error, context?: Record<string, unknown>) {
  Sentry.captureException(error, {
    extra: context,
  })
}

/**
 * Track payment-related events for monitoring.
 */
export function trackPaymentEvent(
  action: 'started' | 'succeeded' | 'failed',
  context?: Record<string, unknown>
) {
  Sentry.addBreadcrumb({
    category: 'payment',
    message: `Payment ${action}`,
    level: action === 'failed' ? 'error' : 'info',
    data: context,
  })
}

// Emits a code-free countable event while legacy JWT query links remain
// supported during the compatibility window.
export function trackLegacyPortalBootstrap(resourceType: 'lead' | 'booking') {
  Sentry.captureMessage('portal_legacy_jwt_bootstrap', {
    level: 'info',
    tags: { resource_type: resourceType },
  })
}

// Re-export Sentry for direct usage
export { Sentry }
